import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import axios from 'axios';
import _ from 'lodash';

import Sidebar from 'react-sidebar';

import SidebarContent from '../../common/sidebar/SidebarContent';
import useSidebar from '../../common/sidebar/useSidebar';

import Loader from '../../common/loader/Loader';

import DesktopHeader from '../DesktopHeader';

import CourseLabel from './CourseLabel';
import CourseLevelLabel from './CourseLevelLabel';
import CourseSection from './CourseSection';
import CoursePrice from './CoursePrice';

import DesktopFooter from '../footer/DesktopFooter';

import { getDesktopSidebarStyles } from '../../../utils/sidebar-utils';

const useCourseInfoPageState = courseId => {
  const [courseData, setCourseData] = useState(null);

  useEffect(() => {
    axios.get(`/api/v1/courses/${courseId}`).then(response => {
      const data = response.data.course;

      const webinarsCount = _.sumBy(data.sections, x => x.webinars.length);

      setCourseData({ ...data, webinarsCount });
    });
  }, []);

  return { courseData };
};

const CourseInfoPage = props => {
  const { courseData } = useCourseInfoPageState(props.match.params.id);

  const { isSidebarOpen, setIsSidebarOpen, openSidebar, closeSidebar } = useSidebar();

  if (!courseData) {
    return <Loader isVisible={true} />;
  }

  const courseInfoClassNames = classnames('course-info', {
    'is-course-csharp': courseData.type === 1,
    'is-course-javascript': courseData.type === 2,
    'is-course-typescript': courseData.type === 3,
    'is-course-nodejs': courseData.type === 4,
  });

  const colorClassNames = classnames({
    'is-csharp': courseData.type === 1,
    'is-javascript': courseData.type === 2,
    'is-typescript': courseData.type === 3,
    'is-nodejs': courseData.type === 4,
  });

  return (
    <Sidebar
      open={isSidebarOpen}
      onSetOpen={setIsSidebarOpen}
      sidebar={<SidebarContent closeSidebar={closeSidebar} />}
      styles={getDesktopSidebarStyles()}
    >
      <div className={courseInfoClassNames}>
        <DesktopHeader openSidebar={openSidebar} isPrimary />

        <div className="tutorium-container">
          <p className="course-info-title">{courseData.name}</p>
          <p className="course-info-description">{courseData.description}</p>

          <div className="is-flex justify-content-center mt-50px">
            <CourseLabel icon="img/icons/calendar.svg" text={`${courseData.duration} months`} />
            <CourseLabel icon="img/icons/webinar-white.svg" text={`${courseData.webinarsCount} webinars`} />
            <CourseLabel icon="img/icons/checkbox-checked.svg" text={`${courseData.tasksCount} tasks`} />
            <CourseLevelLabel level={courseData.level} />
          </div>
          <div className="mt-90px">
            <div className="course-program-container">
              <p className="course-program-title">Course program</p>

              <div className="course-sections mt-60px">
                {courseData.sections.map((section, sectionIndex) => (
                  <CourseSection
                    key={section.name}
                    index={sectionIndex + 1}
                    section={section}
                    colorClassNames={colorClassNames}
                    type={courseData.type}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        <CoursePrice
          courseId={courseData._id}
          courseName={courseData.name}
          level={courseData.level}
          oldPrice={courseData.oldPrice}
          price={courseData.price}
          colorClassNames={colorClassNames}
          type={courseData.type}
        />

        <DesktopFooter />
      </div>
    </Sidebar>
  );
};

CourseInfoPage.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
};

export default CourseInfoPage;
